import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LuTrash2 } from "react-icons/lu";

import Button from "../../components/button/Button";
import { OutlineButton } from "../../components/button/OutlineButton";
import { StyledDatePicker } from "../../components/datepicker/datepicker";
import InputTime from "../../components/inputs/inputTime";
import { SettingsConfirmModal } from "../../components/modal/SettingsConfirmModal";
import CustomSelect from "../../components/select/Select";
import {
  convertTimeToSeconds,
  getInitialsFromFullName,
  timeFormatter,
} from "../../constants/constants";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import {
  deleteTime,
  getAllTimes,
  getAllUsers,
  updateTime,
} from "../../services/AppService";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { ITime, SelectOptionType } from "../../types/types";
import { transformDateToUTC } from "../../utils/transformDate";
import { BillableSwitch } from "./BillableSwitch";
import { EDIT_TIME_DATE_PROBLEM, durationProblem } from "./editTime.utils";

interface IEditTimeProps {
  closeModal: () => void;
  onSuccess: () => Promise<void> | void;
  time: ITime;
  /** Called once the entry is deleted (the list hides it at once). */
  onDeleted?: (id: string) => void;
}

type FormValues = {
  assignedUserId: string;
  date: Date;
};

type EditableTimeEntry = ITime & {
  workTask?: {
    id: string;
    name: string;
  } | null;
  jobTaskId?: string | null;
};

const READONLY_INPUT_CLASS =
  "input input-bordered flex h-8 w-full items-center border-[#D4D6D9] bg-gray-100 text-sm font-medium disabled:border-[#D4D6D9] disabled:bg-gray-100";

const FIELD_LABEL_CLASS = "label-text mb-2 font-medium text-[#1D2C3B]";
const ACTION_BUTTON_CLASS =
  "h-8 min-h-0 !w-[100px] px-0 py-0 text-sm font-medium";

const EditTime: React.FC<IEditTimeProps> = ({
  closeModal,
  time,
  onSuccess,
  onDeleted,
}) => {
  const dispatch = useAppDispatch();

  const { users, isLoading, user } = useAppSelector(
    (state) => state.appReducer,
  );
  const editableTime = time as EditableTimeEntry;

  const contactName = useMemo(() => {
    if (time.contactId) {
      return `${time.contact?.firstName || ""} ${
        time.contact?.lastName || ""
      }`.trim();
    }

    return time.businessContact?.companyName || "";
  }, [time]);

  const isSubtaskTime = Boolean(
    editableTime.jobTaskId || editableTime.workTask,
  );
  const timerTypeLabel = isSubtaskTime ? "Subtask" : "Work";
  const taskName = isSubtaskTime
    ? editableTime.workTask?.name || "N/A"
    : time.work?.name || "N/A";

  const usersOptions = useMemo<SelectOptionType[]>(
    () =>
      users.map((userItem) => {
        const fullName = [
          userItem.userProfile?.firstName,
          userItem.userProfile?.lastName,
        ]
          .filter(Boolean)
          .join(" ");

        return {
          avatar: getInitialsFromFullName(fullName),
          label: fullName,
          value: userItem.id,
          type: "custom",
          avatarCustomColor: userItem.userProfile?.color,
        };
      }),
    [users],
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError: setFormError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      assignedUserId: time.assignedUserId,
      date: time.date ? new Date(time.date) : new Date(),
    },
  });
  const assignedUserId = watch("assignedUserId");

  const selectedUser = useMemo(
    () =>
      usersOptions.find((option) => option.value === assignedUserId) || null,
    [assignedUserId, usersOptions],
  );

  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const [notes, setNotes] = useState(time?.notes || "");
  const [isBillable, setIsBillable] = useState(time?.isBillable ?? true);
  const [duration, setDuration] = useState({
    h:
      timeFormatter(time?.duration || 0)
        .split(" ")?.[0]
        ?.slice(0, -1) || "0",
    m:
      timeFormatter(time?.duration || 0)
        .split(" ")?.[1]
        ?.slice(0, -1) || "0",
  });

  // QA batch 12: duration checks with messages (letters, 0, > 24 h, minutes > 59).
  const [error, setError] = useState("");

  useEffect(() => {
    dispatch(getAllUsers(getLinkedFirm()?.orgId));
  }, [dispatch]);

  useEffect(() => {
    setDuration({
      h:
        timeFormatter(time?.duration || 0)
          .split(" ")?.[0]
          ?.slice(0, -1) || "0",
      m:
        timeFormatter(time?.duration || 0)
          .split(" ")?.[1]
          ?.slice(0, -1) || "0",
    });

    setNotes(time?.notes || "");
    setIsBillable(time?.isBillable ?? true);
    setValue("date", time.date ? new Date(time.date) : new Date());
    setValue("assignedUserId", time.assignedUserId);
  }, [time, setValue]);

  const onSubmit = async (data: FormValues) => {
    const problem = durationProblem(duration);
    if (problem) {
      setError(problem);
      return;
    }
    const result = await dispatch(
      updateTime({
        id: time.id,
        workId: time.workId || null,
        assignedUserId: data.assignedUserId,
        contactId: time.contactId,
        businessContactId: time.businessContactId || null,
        duration: convertTimeToSeconds(duration),
        notes,
        date: moment(data.date).utc(),
        isBillable,
      }),
    );
    if (updateTime.rejected.match(result)) {
      setError("Couldn't save the time. Check the duration and try again.");
      return;
    }

    await onSuccess();
    closeModal();
  };

  const onDelete = async () => {
    setIsLoadingDelete(true);

    // QA batch 15: wait for the delete; refreshing before it finished left the
    // deleted row in the list until the next reload.
    const result = await dispatch(deleteTime(time));
    if (deleteTime.fulfilled.match(result)) onDeleted?.(time.id);
    await onSuccess();

    await dispatch(
      getAllTimes({
        id: getLinkedFirm()?.orgId,
        userId: user.id,
      }),
    );

    setIsLoadingDelete(false);
    closeModal();
  };

  return (
    <>
      <div
        id={"edit-time-modal"}
        className={
          "fixed left-0 top-0 z-[4] flex h-screen w-screen items-center justify-center bg-gray-500/20"
        }>
        <div
          onClick={(event) => event.stopPropagation()}
          className={
            "max-h-[750px] w-full max-w-lg overflow-hidden rounded-[10px] bg-white font-Manrope"
          }>
          <div
            className={
              "grid grid-cols-[1fr_max-content] border-b border-gray-300 px-6 py-2 text-center text-base font-semibold"
            }>
            <div className={"flex items-center justify-start text-[16px]"}>
              Edit Time
            </div>
            <button
              type={"button"}
              className={"close-popup cursor-pointer text-end"}
              onClick={closeModal}>
              <img src={"/img/closse.png"} alt={""} />
            </button>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={
              "max-h-[calc(750px-41px)] w-full overflow-auto px-6 pb-6 pt-4"
            }>
            <div>
              <div className={"mb-4"}>
                <div className={FIELD_LABEL_CLASS}>Contact</div>
                <input
                  className={READONLY_INPUT_CLASS}
                  value={contactName || "N/A"}
                  disabled
                />
              </div>

              <div className={"mb-4 grid grid-cols-[1fr_2.2fr] gap-4"}>
                <div>
                  <div className={FIELD_LABEL_CLASS}>Type</div>
                  <input
                    className={READONLY_INPUT_CLASS}
                    value={timerTypeLabel}
                    disabled
                  />
                </div>
                <div>
                  <div className={FIELD_LABEL_CLASS}>Task Name</div>
                  <input
                    className={READONLY_INPUT_CLASS}
                    value={taskName}
                    disabled
                  />
                </div>
              </div>

              <div className={"mb-4"}>
                <div className={FIELD_LABEL_CLASS}>Logged By</div>
                <CustomSelect
                  options={usersOptions}
                  value={selectedUser}
                  onChange={(newValue) => {
                    const nextUser = newValue as SelectOptionType;
                    setValue("assignedUserId", nextUser.value);
                  }}
                  withAvatar
                  withMenu
                  isLoading={isLoading}
                  selectStyle={{
                    fullWidth: true,
                    small: true,
                    width: "464px",
                    fontWeight: 500,
                  }}
                />
              </div>

              {/* Date column at least 170px so "Aug 25, 2025" isn't cut off (QA C6). */}
              <div
                className={"mb-4 grid grid-cols-[minmax(170px,1fr)_2fr] gap-4"}>
                <div className={"w-full"}>
                  <div className={FIELD_LABEL_CLASS}>Date</div>
                  <Controller
                    control={control}
                    name={"date"}
                    rules={{
                      // QA batch 15: an emptied or unreadable date says so.
                      validate: (value) =>
                        (Boolean(value) &&
                          !Number.isNaN(new Date(value).getTime())) ||
                        EDIT_TIME_DATE_PROBLEM,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <StyledDatePicker
                        value={value ? new Date(value) : null}
                        format={"MMM d, yyyy"}
                        placeholder={"MMM D, YYYY"}
                        mountNode={"edit-time-modal"}
                        size={"sm"}
                        showCalendarIcon
                        acceptTypedDates
                        onChange={(date) => {
                          clearErrors("date");
                          onChange(transformDateToUTC(date));
                        }}
                        onClear={() => onChange(null)}
                        onInvalidTyped={() => {
                          // Not saved with the old date: the box is emptied
                          // and Save says what's wrong.
                          onChange(null);
                          setFormError("date", {
                            message: EDIT_TIME_DATE_PROBLEM,
                          });
                        }}
                      />
                    )}
                  />
                  {errors?.date?.message && (
                    <div className={"pt-2 text-sm font-bold text-error"}>
                      {errors.date.message}
                    </div>
                  )}
                </div>

                <div className={"w-full font-medium"}>
                  <div className={FIELD_LABEL_CLASS}>Duration</div>
                  <InputTime
                    value={duration}
                    onChange={(next) => {
                      setDuration(next);
                      setError("");
                    }}
                    onProblem={(message) => message && setError(message)}
                    inputClassName={"h-8 text-sm font-medium"}
                  />
                  {error && (
                    <div className={"pt-2 text-sm font-bold text-error"}>
                      {error}
                    </div>
                  )}
                </div>
              </div>

              <BillableSwitch
                value={isBillable}
                onChange={setIsBillable}
                className={"mb-4"}
                disabledReason={
                  time.isBilled ? "Already on an invoice." : undefined
                }
              />

              <div className={"mb-4"}>
                <div className={FIELD_LABEL_CLASS}>Notes</div>
                <textarea
                  value={notes}
                  placeholder={"Add a note...(optional)"}
                  className={
                    "input input-bordered min-h-[100px] w-full py-[8px] text-sm font-medium placeholder:text-gray-400"
                  }
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>

              <div className={"flex justify-between"}>
                <Button
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  buttonType={"button"}
                  colorType={"error"}
                  label={"Delete"}
                  isLoading={isLoadingDelete}
                  disabled={isLoadingDelete}
                  size={"custom"}
                  extraClasses={`normal-case ${ACTION_BUTTON_CLASS}`}
                />

                <div className={"flex space-x-[10px]"}>
                  <OutlineButton
                    colorType={"neutral"}
                    label={"Cancel"}
                    onClick={closeModal}
                    size={"small"}
                    extraClasses={`normal-case ${ACTION_BUTTON_CLASS} border-[#D4D6D9] bg-white text-[#253747]`}
                  />
                  <Button
                    buttonType={"submit"}
                    label={"Save"}
                    size={"custom"}
                    extraClasses={`normal-case ${ACTION_BUTTON_CLASS}`}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {isDeleteConfirmOpen && (
        <SettingsConfirmModal
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={onDelete}
          icon={<LuTrash2 size={24} />}
          title={"Delete this time entry?"}
          description={"Do you want to permanently delete this time entry?"}
          confirmText={"Delete"}
          isLoading={isLoadingDelete}
        />
      )}
    </>
  );
};

export default EditTime;
